import { User } from '../user';
import { CHAT_STATUS, ChatAccessType } from './constants';
import { IChat, IChatChannelInfo } from './interfaces';
import { Permissions } from '../permissions';

import Permission = Permissions.Permission;

/**
 * Check is specific user has specific access to specific chat.
 *
 * **Chats set as user contacts granted full access**
 *
 */
export async function chatAccess<IsJSON extends boolean = true>(
    chat: IChat<IsJSON>,
    user: User.User<IsJSON>,
    accessType: ChatAccessType,
    /**
     * Get all permissions of specified roles
     */
    getRolesPermissions: (rolesNames: string[]) => Promise<Permission[]> | Permission[],
    /**
     * Get departments ids of specified user
     */
    getUserDepartmentsIds: (uid: string) => Promise<string[]> | string[],
): Promise<boolean> {

    if (accessType === ChatAccessType.send) {
        // Minimal requirement for send access
        if (!(chat.status === CHAT_STATUS.ASSIGNED && chat.agent.uid === user._id)) {
            return false;
        }
    }

    // Grant full access for chats that are user contacts
    if (isUserContactChat(chat.channelInfo, user.contacts)) {
        return true;
    }

    // Chat access set directly
    if (user.chatsAccess && user.chatsAccess.length) {
        if (user.chatsAccess.some((filter) => {
            return filter.name === chat.channelInfo.name
                && filter.accountId === chat.channelInfo.accountId
                && filter.type.includes(accessType)
                && (
                    !filter.id
                    || filter.id === chat.channelInfo.id
                );
        })) {
            return true;
        }
    }

    /** All permissions of the user */
    const allPermissions = await getRolesPermissions(user.roles);

    // Firstly we check access that not need to load additional data (departments)

    if (accessType === ChatAccessType.send) { // ID and status we check at the top
        return allPermissions.includes(Permission.chats_assigned_send);
    }

    if (accessType === ChatAccessType.list) {
        // If chat bind to user, user always have list access to it
        if (chat.agent.uid === user._id) {
            return true;
        }
        if (allPermissions.includes(Permission.chats_all_list)) {
            return true;
        }
    }

    if (accessType === ChatAccessType.manage) {
        if (chat.agent.uid === user._id) {
            if (allPermissions.includes(Permission.chats_binded_manage)) {
                return true;
            }
        }
        if (allPermissions.includes(Permission.chats_all_manage)) {
            return true;
        }
    }

    if (accessType === ChatAccessType.view) {
        if (chat.agent.uid === user._id) {
            return true;
        }
        if (allPermissions.includes(Permission.chats_all_view)) {
            return true;
        }
    }

    if (!!chat.departmentId) {
        const departments = await getUserDepartmentsIds(user._id);
        // Chat has agent's department
        if (departments.includes(chat.departmentId)) {
            if (accessType === ChatAccessType.list && allPermissions.includes(Permission.chats_own_department_list)) {
                return true;
            }
            if (accessType === ChatAccessType.manage && allPermissions.includes(Permission.chats_own_department_manage)) {
                return true;
            }
            if (accessType === ChatAccessType.view && allPermissions.includes(Permission.chats_own_department_view)) {
                return true;
            }
        }
    } else {
        if (accessType === ChatAccessType.list && allPermissions.includes(Permission.chats_no_department_list)) {
            return true;
        }
        if (accessType === ChatAccessType.manage && allPermissions.includes(Permission.chats_no_department_manage)) {
            return true;
        }
        if (accessType === ChatAccessType.view && allPermissions.includes(Permission.chats_no_department_view)) {
            return true;
        }
        if (!chat.agent.uid) {
            if (accessType === ChatAccessType.list && allPermissions.includes(Permission.chats_unbinded_list)) {
                return true;
            }
            if (accessType === ChatAccessType.manage && allPermissions.includes(Permission.chats_unbinded_manage)) {
                return true;
            }
            if (accessType === ChatAccessType.view && allPermissions.includes(Permission.chats_unbinded_view)) {
                return true;
            }
        }
    }

    return false;
}

export function isUserContactChat(chatChannelInfo: IChatChannelInfo, userContacts: User.User['contacts']): boolean {
    if (userContacts && userContacts.length) {
        if (userContacts.some((contact) => {
            return contact.name === chatChannelInfo.name
                && contact.accountId === chatChannelInfo.accountId
                && contact.id === chatChannelInfo.id;
        })) {
            return true;
        }
    }
    return false;
}

export * from './constants';
export * from './interfaces';
